import React, { useState } from 'react';
import {
  Box, Typography, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { getApolloApptRange, recalcApolloApptRange } from '../../api';

// The headers fetch once on mount and nothing revalidates, so a browser left open would
// show a window that someone else's risk priority change has since moved. Rather than put
// a possibly stale range on screen, show a button and fetch the live value on each open.
const ApptRangeButton = ({ patientId, username, token, moduleCategory = 'Header' }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [range, setRange] = useState(null);
  const [error, setError] = useState('');

  const displayLabel = (value) =>
    value === 'Palliative' ? 'Palliative E' : value === 'Hospice' ? 'Hospice E' : value;

  // The backend nulls out the stringified pandas nulls its pipeline writes ("NaT"), but
  // check here too: a sentinel is truthy, so a falsy-only guard would render it as a date.
  const isRealDate = (value) =>
    !!value && !Number.isNaN(new Date(value).getTime());
  const hasRange = (r) =>
    !!r && isRealDate(r.RecommendedNextAppt) && isRealDate(r.RecommendedNextApptMax);

  const openRange = async () => {
    setOpen(true);
    setLoading(true);
    setError('');
    setRange(null);
    try {
      setRange(await getApolloApptRange(patientId, { username, token }, moduleCategory));
    } catch (err) {
      setError('Failed to load the recommended appointment range.');
    } finally {
      setLoading(false);
    }
  };

  // The stored window is measured from the patient's last visit date, so it drifts out of
  // date between visits. This rebases it onto today and saves the result.
  const recalcRange = async () => {
    setSaving(true);
    setError('');
    try {
      setRange(await recalcApolloApptRange(patientId, { username, token }, moduleCategory));
    } catch (err) {
      setError('Failed to recalculate the recommended appointment range.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button size="small" variant="outlined" onClick={openRange} sx={{ textTransform: 'none' }}>
        Recommended Appt Range
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Recommended Appt Range</DialogTitle>
        <DialogContent sx={{ px: 1.5 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
          ) : (
            <Box>
              {/* Keep the current range on screen when a recalculate fails, so the error
                  does not wipe out what the user was looking at. */}
              {error && (
                <Typography variant="body2" color="error" sx={{ mb: range ? 1 : 0 }}>
                  {error}
                </Typography>
              )}
              {range && (
                <>
                  {hasRange(range) ? (
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {range.RecommendedNextAppt} to {range.RecommendedNextApptMax}
                    </Typography>
                  ) : (
                    <Typography variant="body2">No recommended appointment range available.</Typography>
                  )}
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    Risk priority: {displayLabel(range.Priority) || '—'}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={recalcRange}
            disabled={loading || saving || !range || !range.PatientID}
            startIcon={saving ? <CircularProgress size={14} /> : null}
            sx={{ textTransform: 'none', mr: 'auto' }}
          >
            {saving ? 'Recalculating…' : 'Recalculate from today'}
          </Button>
          <Button onClick={() => setOpen(false)} disabled={saving}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApptRangeButton;
